import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Shared modal wrapper.
 * - Renders the backdrop + centered card + title bar + close (X) button.
 * - Closes on Esc (calls onClose).
 * - Locks body scroll while open.
 *
 * IMPORTANT: rendered through a React portal directly into `<body>` so the
 * `position: fixed` backdrop is always scoped to the VIEWPORT, never to a
 * transformed ancestor. The page layout uses `animate-fade-in` which
 * applies a CSS transform — without the portal, that transform causes
 * `position: fixed; inset-0` to resolve relative to the transformed
 * container (which can be thousands of pixels tall on long pages like
 * /inventory with 100+ rows), pushing the modal off-screen and making
 * the page look like it "just went grey".
 *
 * Props:
 *   title    string                       — header label
 *   icon     ReactNode (optional)         — small icon shown left of the title
 *   onClose  () => void                   — close handler (X, Esc)
 *   size     "sm" | "md" | "lg" | "xl"    — width preset (default "lg" = max-w-2xl)
 *   children ReactNode                    — modal body
 *   footer   ReactNode (optional)         — sticky footer row (e.g. Cancel/Save)
 */
const SIZE_CLASSES = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-xl",
  xl: "max-w-2xl",
  "2xl": "max-w-3xl",
};

export default function Modal({ title, icon, onClose, size = "xl", children, footer }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const node = (
    <div
      className="fixed inset-0 bg-zinc-900/60 z-50 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="modal-backdrop"
    >
      <div className={`bg-white rounded-xl border border-zinc-200 w-full ${SIZE_CLASSES[size]} shadow-2xl flex flex-col max-h-[92vh]`}>
        <div className="flex items-center justify-between border-b border-zinc-100 px-6 py-4 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {icon}
            <h3 className="font-display text-lg font-semibold truncate">{title}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 p-1" data-testid="modal-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
        {footer && (
          <div className="border-t border-zinc-100 px-6 py-4 flex justify-end gap-3 bg-zinc-50 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // SSR-safe guard — document only exists in the browser.
  if (typeof document === "undefined") return node;
  return createPortal(node, document.body);
}
